import lowerFirst from "lodash-es/lowerFirst";
import toPairs from "lodash-es/toPairs";
import React from "react";

import { UserChip } from "@/mk2/apps/forum/components/UserChip";
import {
    TopicEntity,
    TopicModeratorInvitationEntity,
    TopicModeratorsDetailsEntity,
} from "@/mk2/apps/forum/schemas";
import { forumTopicModeratorInvitationUrl } from "@/mk2/apps/forum/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { ModeratorInvitationListItem } from "@/mk2/components/ModeratorInvitationListItem";
import { ModeratorListItem } from "@/mk2/components/ModeratorListItem";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { UserEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    FORUM_TOPIC_ADD_MODERATOR,
    FORUM_TOPIC_MODERATOR_LEFT_ON_when,
} from "@/mk/autogenerated/translations/TopicModeratorsList.67f5735f164f29a22149c02fa5ad67e5";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./TopicModeratorsList.mscss";

interface OwnProps {
    requestUser: UserEntity;
    requestUserPermissions: string[];

    topic: TopicEntity;
    moderatorsDetails: TopicModeratorsDetailsEntity;

    onInviteModerator(topicSlug: string);
    onRemoveModerator(topicSlug: string, username: string);
    onDeclineInvitation(id: number);
}

type Props = OwnProps;

export class TopicModeratorsList extends React.Component<Props> {
    public render() {
        const { topic, moderatorsDetails, requestUser, requestUserPermissions } = this.props;
        const currentModerators = topic.topicModerators?.map((tm) => tm.user) || topic.moderators;
        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const isModerator = isAuthenticatedAs(requestUser, currentModerators);

        const currentAndPendingModerators: { [username: string]: React.ReactNode } = {};
        for (const u of currentModerators) {
            currentAndPendingModerators[u.username] = this.renderActiveModerator(u);
        }
        for (const mi of moderatorsDetails.pendingInvitations) {
            if (currentAndPendingModerators[mi.invitee.username]) {
                // another invitation for a moderator that is already active
                continue;
            }
            currentAndPendingModerators[mi.invitee.username] = this.renderInvitation(mi);
        }

        const exModerators: { [username: string]: React.ReactNode } = {};
        for (const md of moderatorsDetails.allModerators) {
            if (currentAndPendingModerators[md.user.username]) {
                // active moderator or or has pending invitation
                continue;
            }
            exModerators[md.user.username] = this.renderExModerator(md.user, md.deletedAt);
        }

        const canAdd =
            // moderator moze pozvat dalsieho moderatora (najviac mozu byt traja)
            (isModerator &&
                !topic.deletedAt &&
                Object.keys(currentAndPendingModerators).length < 3) ||
            // admin moze pridat aj ked uz su traja
            (isAdmin && !topic.deletedAt);

        return (
            // zotried podla username, zmazanych nakoniec
            <div className={styles.TopicModerators}>
                {toPairs(currentAndPendingModerators)
                    .sort()
                    .map((p) => p[1])}
                {toPairs(exModerators)
                    .sort()
                    .map((p) => p[1])}

                {canAdd && (
                    <Btn
                        key="add-moderator"
                        type={BtnType.BlueOutline}
                        label={FORUM_TOPIC_ADD_MODERATOR}
                        className={styles.TopicModeratorsList__add}
                        onClick={this.handleOnInviteModerator}
                    />
                )}
            </div>
        );
    }

    private renderActiveModerator(u: UserEntity): React.ReactNode {
        const { topic, requestUser, requestUserPermissions } = this.props;
        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const canDelete =
            // admin moze vymazat aj poslednu moderatorku, tak ze ziadna neostane
            isAdmin ||
            // sama je moderatorka a ak odide, ostane este nejaka ina
            (isAuthenticatedAs(requestUser, u) &&
                (topic.topicModerators?.length || topic.moderators.length) > 1);

        return (
            <ModeratorListItem
                key={`active-${u.username}`}
                className={styles.TopicModeratorsList__listitem}
                user={u}
                id={u.username}
                onRemove={this.onRemoveModerator}
                canRemove={canDelete}
            />
        );
    }

    private onRemoveModerator = (username: string) => {
        const { topic, onRemoveModerator } = this.props;
        onRemoveModerator(topic.slug, username);
    };

    private renderInvitation(mi: TopicModeratorInvitationEntity) {
        const { topic, requestUser, onDeclineInvitation } = this.props;
        const canOpen =
            !topic.deletedAt &&
            // invitee can open the invitation sent to her
            isAuthenticatedAs(requestUser, mi.invitee);
        const canDecline =
            !topic.deletedAt &&
            // inviter can decline the invitation sent from her
            isAuthenticatedAs(requestUser, mi.inviter);

        return (
            <ModeratorInvitationListItem
                key={`invited-${mi.invitee.username}`}
                className={styles.TopicModeratorsList__listitem}
                user={mi.invitee}
                canDecline={canDecline}
                canOpen={canOpen}
                openUrl={url(forumTopicModeratorInvitationUrl, {
                    topicSlug: topic.slug,
                    username: mi.invitee.username,
                })}
                createdAt={mi.createdAt}
                invitationId={mi.id}
                onDecline={onDeclineInvitation}
            />
        );
    }

    private renderExModerator(u: UserEntity, deletedAt: string): React.ReactNode {
        return (
            <div key={`ex-${u.username}`} className={styles.TopicModeratorsList__listitem}>
                <div className={styles.TopicModeratorsList__listitem_user}>
                    <UserChip user={u} disabled />

                    <div className={styles.TopicModeratorsList__listitem_user_note}>
                        {interpolate(FORUM_TOPIC_MODERATOR_LEFT_ON_when, {
                            when: lowerFirst(timeAt(deletedAt)),
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private handleOnInviteModerator = () => {
        const { topic, onInviteModerator } = this.props;
        onInviteModerator(topic.slug);
    };
}
